import {
  DiscoverBuilding,
  DiscoverBuildingUnit,
} from '../../_types/quickpass-discover'
import { QuickPassProfile } from '../../_types/quickpass-profile'

const buildInquireMessageStarter = (
  building: DiscoverBuilding,
  unit: DiscoverBuildingUnit | null,
  profile: QuickPassProfile | null,
): string => {
  const firstName = profile?.firstName ?? ''
  const opener = firstName === '' ? 'Hi!' : `Hi, I'm ${firstName}.`
  const placeName = building.buildingName
  const unitLine =
    unit === null
      ? ''
      : ` I'm interested in ${unit.unitTypeName || unit.unitName || 'a unit'}.`
  const moveInLine =
    profile?.unitPreferences.preferredMoveInDate !== null &&
    profile?.unitPreferences.preferredMoveInDate !== undefined
      ? ` I'm hoping to move in around ${new Date(profile.unitPreferences.preferredMoveInDate).toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' })}.`
      : ''
  return `${opener} I came across ${placeName} on QuickPass and would love to hear more.${unitLine}${moveInLine} When can we connect?`
}

const formatUnitPrice = (unit: DiscoverBuildingUnit): string => {
  const fmt = (value: number): string => `$${value.toLocaleString('en-CA')}`
  if (unit.costLowest > 0 && unit.costHighest > 0) {
    if (unit.costLowest === unit.costHighest) {
      return `${fmt(unit.costLowest)} / month`
    }
    return `${fmt(unit.costLowest)}–${fmt(unit.costHighest)} / month`
  }
  if (unit.costLowest > 0) return `${fmt(unit.costLowest)} / month`
  if (unit.costHighest > 0) return `${fmt(unit.costHighest)} / month`
  return 'Price on request'
}

const formatUnitBedsBaths = (unit: DiscoverBuildingUnit): string => {
  const parts: string[] = []
  if (unit.bedrooms === 0) {
    parts.push('Studio')
  } else if (unit.bedrooms > 0) {
    parts.push(
      `${unit.bedrooms.toString()} bed${unit.bedrooms === 1 ? '' : 's'}`,
    )
  }
  if (unit.bathrooms > 0) {
    parts.push(
      `${unit.bathrooms.toString()} bath${unit.bathrooms === 1 ? '' : 's'}`,
    )
  }
  if (unit.squareFootage > 0) {
    parts.push(`${unit.squareFootage.toLocaleString('en-CA')} ft²`)
  }
  return parts.join(' · ')
}

const UNIT_AVAILABILITY_LABELS: Record<
  DiscoverBuildingUnit['availability'],
  string
> = {
  available: 'Available',
  'available-soon': 'Available soon',
  occupied: 'Occupied',
  unavailable: 'Off-market',
}

const getUnitStatusLabel = (
  availability: DiscoverBuildingUnit['availability'],
): string => {
  return UNIT_AVAILABILITY_LABELS[availability] ?? 'Status unknown'
}

const BUILDING_STATUS_LABELS: Record<
  DiscoverBuilding['availabilitySummary'],
  string
> = {
  available: 'Units available',
  'available-soon': 'Units available soon',
  occupied: 'Currently fully occupied',
  unavailable: 'Off-market',
}

const getBuildingStatusLabel = (
  availability: DiscoverBuilding['availabilitySummary'],
): string => {
  return BUILDING_STATUS_LABELS[availability] ?? 'Status unknown'
}

const formatBuildingPriceRange = (building: DiscoverBuilding): string => {
  const fmt = (value: number): string => `$${value.toLocaleString('en-CA')}`
  if (building.costMin <= 0 && building.costMax <= 0) {
    return 'Price on request'
  }
  if (
    building.costMin > 0 &&
    building.costMax > 0 &&
    building.costMin !== building.costMax
  ) {
    return `${fmt(building.costMin)} – ${fmt(building.costMax)} / month`
  }
  const value = building.costMin > 0 ? building.costMin : building.costMax
  return `${fmt(value)} / month`
}

const formatBuildingBeds = (building: DiscoverBuilding): string => {
  if (building.bedroomsList.length === 0) return ''
  const labels = building.bedroomsList.map(count => {
    if (count === 0) return 'studio'
    return `${count.toString()}-bed`
  })
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return `${labels[0]} & ${labels[1]}`
  return `${labels.slice(0, -1).join(', ')}, ${labels[labels.length - 1]}`
}

/**
 * Maps a URL `:listingId` segment into the building-key shape the server
 * expects. New code emits `building:<id>` / `community:<id>` /
 * `inventory:<id>` and we URL-encode the colon, so we URL-decode first.
 * Legacy URLs (saved-search notifications, the v1 detail page) used a
 * raw inventory id with no prefix — we treat those as `inventory:<id>`.
 *
 * @param {string} raw - the raw URL param
 * @returns {string} the canonical building key
 */
const decodeBuildingKey = (raw: string): string => {
  const decoded = decodeURIComponent(raw)
  if (decoded.includes(':') === true) {
    return decoded
  }
  return `inventory:${decoded}`
}

export {
  buildInquireMessageStarter,
  formatUnitPrice,
  formatUnitBedsBaths,
  getUnitStatusLabel,
  getBuildingStatusLabel,
  formatBuildingPriceRange,
  formatBuildingBeds,
  decodeBuildingKey,
}
